// Position-certainty for the knockout bracket. While the group stage is still
// running, the bracket only PROJECTS its 32 Round-of-32 entrants from the live
// standings (see resolveBracket). This module answers, for each projected team:
// is its bracket slot 100% locked — i.e. does it hold that exact slot in EVERY
// way the remaining group matches could finish?
//
// Pure and DB-free, like knockout.ts — web and native both render off it.
//
// Soundness rule: every approximation here only ever ADDS possible outcomes,
// never drops one. So a team's computed set of possible finishing positions is
// always a SUPERSET of the true set; a singleton superset ⟹ truly locked. That
// is what lets us ignore the unbounded tiebreakers (goal difference, goals,
// conduct, FIFA rank) safely — they can only FIX orderings we already leave
// open, never invent a lock. A false ✓ is the one harmful output; a cautious ?
// is fine.

import { qualifyingThirdPlaceIds, rankThirdPlacedTeams, type StandingRow } from './standings';
import { THIRD_PLACE_ALLOCATION } from './third-place-allocation';
import { WINNER_ORDER, type BracketGroup } from './knockout';
import type { MatchPublic, TeamPublic } from './types';

// A group match reduced to who played whom and its result sign. Pending (not
// finished — live counts as pending, its score isn't final) matches carry a
// null sign and get enumerated; finished matches carry their fixed sign.
type Sign = 'win1' | 'draw' | 'win2';
interface GroupMatch {
  i1: number; // index of team1 within the group's 4-team array (= current standing slot)
  i2: number;
  sign: Sign | null; // null ⟹ pending
}

interface GroupAnalysis {
  // posSets[i] = every finishing position (1-based) team i can still reach.
  posSets: Set<number>[];
  // Points the eventual 3rd-placed team could finish on (a safe range: min is a
  // lower bound, max an upper bound on the real 3rd-place points).
  thirdMinPts: number;
  thirdMaxPts: number;
  fullyFinished: boolean;
}

function signOf(m: MatchPublic): Sign | null {
  if (m.status !== 'finished' || !m.result) return null;
  const { team1, team2 } = m.result.fullTime;
  return team1 > team2 ? 'win1' : team1 === team2 ? 'draw' : 'win2';
}

// Points the four teams hold under one concrete assignment of signs to matches.
function pointsFor(n: number, gms: GroupMatch[], signs: (Sign | null)[]): number[] {
  const pts = new Array<number>(n).fill(0);
  gms.forEach((m, k) => {
    const s = signs[k];
    if (s === 'win1') pts[m.i1]! += 3;
    else if (s === 'win2') pts[m.i2]! += 3;
    else if (s === 'draw') {
      pts[m.i1]! += 1;
      pts[m.i2]! += 1;
    }
  });
  return pts;
}

// Ordered cells for one sign assignment: teams partitioned by overall points,
// then within a points-tie by head-to-head points among the tied teams. Both
// keys are margin-independent and orderGroupStandings never reorders across a
// head-to-head-points boundary — so a higher cell is ALWAYS above a lower one.
// Teams sharing a cell are left mutually swappable (their finer separation —
// h2h GD/GF, overall GD/GF, … — is margin-dependent, hence not guaranteed).
function cellsFor(n: number, gms: GroupMatch[], signs: (Sign | null)[], pts: number[]): number[][] {
  const order = [...Array(n).keys()].sort((a, b) => pts[b]! - pts[a]!);
  const cells: number[][] = [];
  let i = 0;
  while (i < order.length) {
    let j = i + 1;
    while (j < order.length && pts[order[j]!]! === pts[order[i]!]!) j += 1;
    const block = order.slice(i, j);
    if (block.length === 1) {
      cells.push(block);
    } else {
      const inBlock = new Set(block);
      const h2h = new Map<number, number>(block.map((t) => [t, 0]));
      gms.forEach((m, k) => {
        if (!inBlock.has(m.i1) || !inBlock.has(m.i2)) return;
        const s = signs[k];
        if (s === 'win1') h2h.set(m.i1, h2h.get(m.i1)! + 3);
        else if (s === 'win2') h2h.set(m.i2, h2h.get(m.i2)! + 3);
        else if (s === 'draw') {
          h2h.set(m.i1, h2h.get(m.i1)! + 1);
          h2h.set(m.i2, h2h.get(m.i2)! + 1);
        }
      });
      const sub = [...block].sort((a, b) => h2h.get(b)! - h2h.get(a)!);
      let k = 0;
      while (k < sub.length) {
        let l = k + 1;
        while (l < sub.length && h2h.get(sub[l]!)! === h2h.get(sub[k]!)!) l += 1;
        cells.push(sub.slice(k, l));
        k = l;
      }
    }
    i = j;
  }
  return cells;
}

function analyzeGroup(n: number, gms: GroupMatch[]): GroupAnalysis {
  const posSets: Set<number>[] = Array.from({ length: n }, () => new Set<number>());
  let thirdMinPts = Infinity;
  let thirdMaxPts = -Infinity;
  const recordThird = (p: number) => {
    if (p < thirdMinPts) thirdMinPts = p;
    if (p > thirdMaxPts) thirdMaxPts = p;
  };

  const pendingIdx = gms.flatMap((m, k) => (m.sign === null ? [k] : []));

  if (pendingIdx.length === 0) {
    // Fully finished: the standings are final, so each team's slot equals its
    // current position. (The cell model would wrongly merge GD-separated teams.)
    for (let i = 0; i < n; i++) posSets[i]!.add(i + 1);
    recordThird(pointsFor(n, gms, gms.map((m) => m.sign))[2]!);
    return { posSets, thirdMinPts, thirdMaxPts, fullyFinished: true };
  }

  const SIGNS: Sign[] = ['win1', 'draw', 'win2'];
  const total = 3 ** pendingIdx.length;
  for (let combo = 0; combo < total; combo++) {
    const signs = gms.map((m) => m.sign);
    let c = combo;
    for (const k of pendingIdx) {
      signs[k] = SIGNS[c % 3]!;
      c = Math.floor(c / 3);
    }
    const pts = pointsFor(n, gms, signs);
    const cells = cellsFor(n, gms, signs, pts);
    let above = 0;
    for (const cell of cells) {
      const minPos = above + 1;
      const maxPos = above + cell.length;
      const canBeThird = minPos <= 3 && 3 <= maxPos;
      for (const ti of cell) {
        for (let p = minPos; p <= maxPos; p++) posSets[ti]!.add(p);
        if (canBeThird) recordThird(pts[ti]!);
      }
      above += cell.length;
    }
  }
  return { posSets, thirdMinPts, thirdMaxPts, fullyFinished: false };
}

// All k-element subsets of arr (k ≤ arr.length ≤ 12, so ≤ C(12,8)=495).
function* combinations<T>(arr: T[], k: number): Generator<T[]> {
  if (k === 0) {
    yield [];
    return;
  }
  if (k > arr.length) return;
  const [head, ...rest] = arr;
  for (const c of combinations(rest, k - 1)) yield [head!, ...c];
  yield* combinations(rest, k);
}

/**
 * Team ids whose projected Round-of-32 slot is 100% locked given the current
 * standings and which group matches are still unfinished. `groups` is the same
 * live-aware value passed to resolveBracket (rows ordered best-first); `matches`
 * is the full match list (group matches are re-filtered here for the
 * sign-enumeration). A team not in the returned set still has a movable slot.
 */
export function lockedR32SlotTeamIds(groups: BracketGroup[], matches: MatchPublic[]): Set<string> {
  const locked = new Set<string>();
  const byLetter = new Map<string, BracketGroup>(groups.map((g) => [g.group, g]));

  // Group matches bucketed by group letter.
  const gmByLetter = new Map<string, MatchPublic[]>();
  for (const m of matches) {
    if (m.stage !== 'group' || !m.group) continue;
    (gmByLetter.get(m.group) ?? gmByLetter.set(m.group, []).get(m.group)!).push(m);
  }

  // Per-group sign-enumeration analysis.
  const analysis = new Map<string, GroupAnalysis>();
  for (const g of groups) {
    const idx = new Map(g.rows.map((r, i) => [r.team.id, i]));
    const gms: GroupMatch[] = [];
    for (const m of gmByLetter.get(g.group) ?? []) {
      if (!m.team1 || !m.team2) continue;
      const i1 = idx.get(m.team1.id);
      const i2 = idx.get(m.team2.id);
      if (i1 === undefined || i2 === undefined) continue;
      gms.push({ i1, i2, sign: signOf(m) });
    }
    analysis.set(g.group, analyzeGroup(g.rows.length, gms));
  }

  // 1) Winner (slot 1X) and runner-up (slot 2X) slots — locked iff the current
  // occupant is guaranteed exactly that position. No cross-group dependency:
  // 1X / 2X are fixed bracket slots.
  for (const g of groups) {
    const a = analysis.get(g.group)!;
    const first = g.rows[0]?.team;
    const second = g.rows[1]?.team;
    if (first && a.posSets[0]!.size === 1 && a.posSets[0]!.has(1)) locked.add(first.id);
    if (second && a.posSets[1]!.size === 1 && a.posSets[1]!.has(2)) locked.add(second.id);
  }

  // 2) Classify each group's third as definitely-in / -out / uncertain for the
  // best-8 cut. "X's third definitely above Y's" iff X's min third-points beats
  // Y's max (points is the first ranking key, and GD is unbounded so it can't
  // safely separate) — or both groups are done and the real comparator says so.
  const letters = groups.map((g) => g.group);
  const definitelyAbove = (x: string, y: string): boolean => {
    const ax = analysis.get(x)!;
    const ay = analysis.get(y)!;
    if (ax.thirdMinPts > ay.thirdMaxPts) return true;
    if (ax.fullyFinished && ay.fullyFinished) {
      const tx = byLetter.get(x)!.rows[2];
      const ty = byLetter.get(y)!.rows[2];
      if (tx && ty) return rankThirdPlacedTeams([tx, ty] as StandingRow[])[0] === tx;
    }
    return false;
  };
  const definiteIn: string[] = [];
  const definiteOut = new Set<string>();
  for (const x of letters) {
    let below = 0;
    let above = 0;
    for (const y of letters) {
      if (x === y) continue;
      if (definitelyAbove(x, y)) below += 1;
      if (definitelyAbove(y, x)) above += 1;
    }
    if (below >= 4) definiteIn.push(x); // ≥4 thirds below ⟹ at worst 8th ⟹ in
    else if (above >= 8) definiteOut.add(x); // ≥8 above ⟹ at best 9th ⟹ out
  }

  // 3) Every 8-group combo consistent with the classification (definite-ins
  // forced in, definite-outs forced out, the rest free). A superset of the
  // truly reachable combos — extra combos only add constraints, so it's safe.
  const uncertain = letters.filter((L) => !definiteIn.includes(L) && !definiteOut.has(L));
  const slotsLeft = 8 - definiteIn.length;
  const possibleCombos: string[] = [];
  if (slotsLeft >= 0 && slotsLeft <= uncertain.length) {
    for (const subset of combinations(uncertain, slotsLeft)) {
      possibleCombos.push([...definiteIn, ...subset].sort().join(''));
    }
  }

  // 4) Third-place slots. The current combo names which group sits in each
  // Annex-C winner column right now (exactly as resolveBracket derives it). A
  // third slot is locked iff its current group is guaranteed 3rd AND that column
  // maps to the same group across EVERY possible combo. Empty combo set (should
  // not happen) ⟹ lock nothing, so the ∀ is never vacuously true.
  const qualifyingIds = qualifyingThirdPlaceIds(groups);
  const currentCombo = groups
    .filter((g) => g.rows[2] && qualifyingIds.has(g.rows[2]!.team.id))
    .map((g) => g.group)
    .sort()
    .join('');
  const assignment = THIRD_PLACE_ALLOCATION[currentCombo];
  if (assignment && possibleCombos.length > 0) {
    for (let col = 0; col < WINNER_ORDER.length; col++) {
      const groupLetter = assignment[col];
      if (!groupLetter) continue;
      const third = byLetter.get(groupLetter)?.rows[2]?.team;
      if (!third) continue;
      const a = analysis.get(groupLetter)!;
      if (!(a.posSets[2]!.size === 1 && a.posSets[2]!.has(3))) continue;
      const columnFixed = possibleCombos.every((k) => THIRD_PLACE_ALLOCATION[k]![col] === groupLetter);
      if (columnFixed) locked.add(third.id);
    }
  }

  return locked;
}
